import * as DialogPrimitive from "@radix-ui/react-dialog";
import { CornerDownLeft, MapPin, Search, Boxes, Truck } from "lucide-react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
} from "react";
import { useNavigate } from "react-router-dom";
import { Kbd } from "@/components/ui/kbd";
import { useLocationData } from "@/hooks/useLocationData";
import { navForWorkspace } from "@/lib/nav";
import { cn } from "@/lib/utils";
import { usePaletteStore } from "@/stores/paletteStore";

interface PaletteItem {
  id: string;
  group: "Pages" | "Assets" | "Shipments" | "Places";
  title: string;
  subtitle?: string;
  href: string;
  icon?: ComponentType<{ className?: string }>;
  keywords?: string;
}

/** ⌘K: jump to a page, a card, or a place. Deliberately simple: substring match. */
export function CommandPalette() {
  const open = usePaletteStore((s) => s.open);
  const setOpen = usePaletteStore((s) => s.setOpen);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-overlay bg-night/40 data-[state=open]:animate-fade-in" />
        <DialogPrimitive.Content
          className="fixed left-1/2 top-[12vh] z-overlay w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 overflow-hidden rounded-md border border-line bg-surface text-ink shadow-xl outline-none data-[state=open]:animate-pop-in"
          aria-describedby={undefined}
        >
          <DialogPrimitive.Title className="sr-only">
            Search
          </DialogPrimitive.Title>
          {/* Mounted only while open, so the query resets every time. */}
          <PaletteBody onDone={() => setOpen(false)} />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function PaletteBody({ onDone }: { onDone: () => void }) {
  const navigate = useNavigate();
  const data = useLocationData();
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  const items = useMemo<PaletteItem[]>(() => {
    const pages: PaletteItem[] = navForWorkspace(
      data.workspace.id === "personal" ? "personal" : "team",
    ).flatMap((e) =>
      "type" in e || e.isExternal
        ? []
        : [
            {
              id: `page-${e.key}`,
              group: "Pages",
              title: e.title,
              href: e.href,
              icon: e.icon,
            },
          ],
    );
    const assets: PaletteItem[] = data
      .listAssets()
      .map((a) => ({
        id: a.id,
        group: "Assets",
        title: a.name,
        subtitle: data.getLastSeenPlace(a.id)?.name,
        href: `/assets/${a.id}`,
        icon: Boxes,
        keywords: data.getDevice(a.deviceId)?.imei,
      }));
    const shipments: PaletteItem[] = data
      .listShipments()
      .map((s) => ({
        id: s.id,
        group: "Shipments",
        title: s.reference,
        subtitle: data.getPlace(s.destinationPlaceId)?.name,
        href: `/shipments/${s.id}`,
        icon: Truck,
      }));
    const places: PaletteItem[] = data
      .listPlaces()
      .map((p) => ({
        id: p.id,
        group: "Places",
        title: p.name,
        href: `/places/${p.id}`,
        icon: MapPin,
      }));
    return [...pages, ...assets, ...shipments, ...places];
  }, [data]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items.filter((i) => i.group !== "Shipments").slice(0, 40);
    return items
      .filter((i) =>
        `${i.title} ${i.subtitle ?? ""} ${i.keywords ?? ""}`
          .toLowerCase()
          .includes(q),
      )
      .slice(0, 60);
  }, [items, query]);

  const activeIndex = Math.min(active, Math.max(0, filtered.length - 1));

  useEffect(() => {
    listRef.current
      ?.querySelector('[data-active="true"]')
      ?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, filtered]);

  const go = (item: PaletteItem | undefined) => {
    if (!item) return;
    onDone();
    navigate(item.href);
  };

  let lastGroup: string | null = null;

  return (
    <>
      <div className="flex items-center gap-2 border-b border-line px-3">
        <Search className="size-4 shrink-0 text-ink-3" aria-hidden="true" />
        <input
          autoFocus
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setActive((a) => Math.min(a + 1, filtered.length - 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setActive((a) => Math.max(a - 1, 0));
            } else if (e.key === "Enter") {
              e.preventDefault();
              go(filtered[activeIndex]);
            }
          }}
          aria-label="Search assets, shipments and places"
          placeholder="Search assets, shipments and places"
          className="h-12 flex-1 bg-transparent text-sm text-ink outline-none placeholder:text-ink-3"
          role="combobox"
          aria-expanded="true"
          aria-controls="palette-list"
          aria-activedescendant={
            filtered[activeIndex]
              ? `palette-${filtered[activeIndex].id}`
              : undefined
          }
        />
        <Kbd>esc</Kbd>
      </div>
      <div
        ref={listRef}
        id="palette-list"
        role="listbox"
        className="max-h-[min(60vh,420px)] overflow-y-auto p-1.5"
      >
        {filtered.length === 0 && (
          <p className="px-3 py-8 text-center text-sm text-ink-3">
            No matches for “{query}”.
          </p>
        )}
        {filtered.map((item, i) => {
          const header = item.group !== lastGroup ? item.group : null;
          lastGroup = item.group;
          const Icon = item.icon;
          return (
            <div key={item.id}>
              {header && (
                <div className="px-2 pb-1 pt-2.5 text-micro font-medium text-ink-3">
                  {header}
                </div>
              )}
              <div
                id={`palette-${item.id}`}
                role="option"
                aria-selected={i === activeIndex}
                data-active={i === activeIndex}
                onMouseMove={() => setActive(i)}
                onClick={() => go(item)}
                className={cn(
                  "flex h-10 cursor-pointer items-center gap-3 rounded-sm px-2 text-sm",
                  i === activeIndex ? "bg-surface-2 text-ink" : "text-ink-2",
                )}
              >
                <span className="flex size-6 items-center justify-center text-ink-3">
                  {Icon && <Icon className="size-4" />}
                </span>
                <span className="truncate font-medium">{item.title}</span>
                {item.subtitle && (
                  <span className="truncate text-xs text-ink-3">
                    {item.subtitle}
                  </span>
                )}
                {i === activeIndex && (
                  <CornerDownLeft
                    className="ml-auto size-3.5 shrink-0 text-ink-3"
                    aria-hidden="true"
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
